/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from '@arco-design/web-react';
import Header from '@/components/Header';
import ResizeWrapper from '@/components/ResizeWrapper';
import Menu from './Menu';
import { useIsMobile } from '@/utils/utils';
import Apis from '@/app/index';
import MainArea from './MainArea';
import { ABORT_VISIBILITY_CHANGE, useLeave } from '@/lib/useCommon';
import { RTCConfig, SceneConfig, updateRTCConfig, updateScene, updateSceneConfig } from '@/store/slices/room';
import styles from './index.module.less';
import globalHxxlConfig from '@/lib/HxxlConfig';

export default function () {

  const leaveRoom = useLeave();
  const dispatch = useDispatch();

  const getScenes = async () => {
    const { scenes, errInfo }: {
      scenes: {
        rtc: RTCConfig;
        scene: SceneConfig;
      }[];
      errInfo: string;
    } = await Apis.Basic.getScenes(globalHxxlConfig);
    if (!scenes || scenes.length === 0) {
      Modal.error({
        title: '错误',
        content: `无法获取有效场景配置，请联系管理员！${errInfo}`,
        closable: true,
        maskClosable: false,
        // 移动端适配：全屏遮罩、垂直居中、禁止滚动
        style: { maxWidth: '90vw', top: '40%' },
        wrapClassName: 'mobile-modal-mask',
      });
    }
    dispatch(updateScene(scenes[0].scene.id));
    dispatch(updateSceneConfig(
      scenes.reduce<Record<string, SceneConfig>>((prev, cur) => {
        prev[cur.scene.id] = cur.scene;
        return prev;
      }, {})
    ));
    dispatch(updateRTCConfig(
      scenes.reduce<Record<string, RTCConfig>>((prev, cur) => {
        prev[cur.scene.id] = cur.rtc;
        return prev;
      }, {})
    ));
  }

  useEffect(() => {
  getScenes();
  const isOriginalDemo = window.location.host.startsWith('localhost');
  
  let countdownTimer: NodeJS.Timeout | null = null;
  
  const handler = () => {
    if (
      document.visibilityState === 'hidden' &&
      !sessionStorage.getItem(ABORT_VISIBILITY_CHANGE)
    ) {
      // 启动60秒倒计时
      countdownTimer = setTimeout(() => {
        leaveRoom();
      }, 60000); // 60秒 = 60000毫秒
    } else if (document.visibilityState === 'visible') {
      // 页面恢复可见时，清除倒计时
      if (countdownTimer) {
        clearTimeout(countdownTimer);
        countdownTimer = null;
      }
    }
  };
  
  !isOriginalDemo && document.addEventListener('visibilitychange', handler);
  
  return () => {
    // 清理倒计时
    if (countdownTimer) {
      clearTimeout(countdownTimer);
    }
    !isOriginalDemo && document.removeEventListener('visibilitychange', handler);
  };
}, []);

  return (
    <ResizeWrapper className={styles.container}>
      <Header />
      <div
        className={styles.main}
        style={{
          padding: useIsMobile() ? '' : '24px',
        }}
      >
        <div className={`${styles.mainArea} ${useIsMobile() ? styles.isMobile : ''}`}>
          <MainArea />
        </div>
        {useIsMobile() ? null : (
          <div className={styles.operationArea}>
            <Menu />
          </div>
        )}
      </div>
    </ResizeWrapper>
  );
}
